import { useCallback } from 'react';
import {
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
  usePostHog,
  useFeatureFlagEnabled as usePostHogFeatureFlagEnabled,
} from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import { FeatureFlagConfig, FeatureFlagKey, FlagInfo } from '@erp/shared-types';
import { useLocalStorageValue } from '@react-hookz/web';
import posthog from 'posthog-js';

function trackFlagInSentry(flag: string, value: boolean | string): void {
  const integration =
    Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
      'FeatureFlags',
    );
  integration?.addFeatureFlag(flag, value);
}

const FEATURE_FLAG_OVERRIDES_STORAGE_KEY = 'ff_overrides';

type FeatureFlagOverrideEntry = {
  flag: FeatureFlagKey;
  flagInfo?: FlagInfo<FeatureFlagKey>;
};

function getStoredFeatureFlagOverrides(): FeatureFlagOverrideEntry[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const raw = window.localStorage.getItem(FEATURE_FLAG_OVERRIDES_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const useFeatureFlagOverrides = () => {
  const { value: flagsWithOverrides, set: setFlagsWithOverrides } =
    useLocalStorageValue<FeatureFlagOverrideEntry[]>(
      FEATURE_FLAG_OVERRIDES_STORAGE_KEY,
      { defaultValue: [] },
    );

  return {
    flagsWithOverrides,
    setFlagsWithOverrides,
  };
};

export function isBooleanFeatureFlagEnabled<Key extends FeatureFlagKey>(
  flag: Key,
  defaultValue: FeatureFlagConfig[Key] & boolean,
): boolean {
  const override = getStoredFeatureFlagOverrides().find((f) => f.flag === flag);

  if (typeof override?.flagInfo?.enabled === 'boolean') {
    trackFlagInSentry(flag, override.flagInfo.enabled);
    return override.flagInfo.enabled;
  }

  const enabled = posthog.isFeatureEnabled(flag);
  const result = typeof enabled === 'boolean' ? enabled : defaultValue;
  trackFlagInSentry(flag, result);
  return result;
}

/**
 * posthog overrides Usage:
 *  - posthog.featureFlags.overrideFeatureFlags(false) // clear all overrides
 *  - posthog.featureFlags.overrideFeatureFlags(['beta-feature']) // enable flags
 *  - posthog.featureFlags.overrideFeatureFlags({'beta-feature': 'variant'}) // set variants
 *  - posthog.featureFlags.overrideFeatureFlags({ // set both flags and payloads
 *      flags: {'beta-feature': 'variant'},
 *      payloads: { 'beta-feature': { someData: true } }
 *    })
 *  - posthog.featureFlags.overrideFeatureFlags({ // only override payloads
 *      payloads: { 'beta-feature': { someData: true } }
 *    })
 */
export function useOverrideFeatureFlags<Key extends FeatureFlagKey>() {
  const { flagsWithOverrides, setFlagsWithOverrides } =
    useFeatureFlagOverrides();

  const ph = usePostHog();

  const setFlagOverridden = useCallback(
    (flag: Key, flagInfo: Partial<FlagInfo<Key>>) => {
      setFlagsWithOverrides((prev) => {
        const existingFlag = prev?.find((f) => f.flag === flag);
        if (existingFlag && prev) {
          return prev.map((f) =>
            f.flag === flag
              ? {
                  ...f,
                  flagInfo: {
                    ...f.flagInfo,
                    ...(flagInfo.enabled !== undefined && {
                      enabled: flagInfo.enabled,
                    }),
                    ...(flagInfo.variantKey !== undefined && {
                      variantKey: flagInfo.variantKey,
                    }),
                    ...(flagInfo.payload !== undefined && {
                      payload: flagInfo.payload,
                    }),
                  } as FlagInfo<FeatureFlagKey>,
                }
              : f,
          );
        }
        return [
          ...(prev ?? []),
          { flag, flagInfo: flagInfo as FlagInfo<FeatureFlagKey> },
        ];
      });
    },
    [setFlagsWithOverrides],
  );

  const toggleFlagEnabled = useCallback(
    (flag: Key, enabled: boolean) => {
      ph.featureFlags.overrideFeatureFlags({
        flags: {
          [flag]: enabled,
        },
      });
      setFlagOverridden(flag, {
        enabled,
      });
    },
    [ph, setFlagOverridden],
  );

  const setVariant = useCallback(
    (flag: Key, variant: string | undefined) => {
      ph.featureFlags.overrideFeatureFlags({ [flag]: variant });
      setFlagOverridden(flag, {
        variantKey: variant as FeatureFlagConfig[Key] extends boolean
          ? undefined
          : string | undefined,
      });
    },
    [ph, setFlagOverridden],
  );

  const setPayload = useCallback(
    (flag: Key, payload: FeatureFlagConfig[Key]) => {
      ph.featureFlags.overrideFeatureFlags({
        payloads: { [flag]: payload },
      });
      setFlagOverridden(flag, {
        payload,
      });
    },
    [ph, setFlagOverridden],
  );

  const clear = useCallback(() => {
    ph.featureFlags.overrideFeatureFlags(false);
    setFlagsWithOverrides([]);
  }, [ph, setFlagsWithOverrides]);

  const removeOverride = useCallback(
    (flag: Key) => {
      ph.featureFlags.overrideFeatureFlags(false);
      setFlagsWithOverrides((prev = []) => prev.filter((f) => f.flag !== flag));
    },
    [ph, setFlagsWithOverrides],
  );

  return {
    clear,
    toggleFlagEnabled,
    setVariant,
    setPayload,
    removeOverride,
    flagsWithOverrides,
  };
}

export function useFeatureFlag<Key extends FeatureFlagKey>(
  flag: Key,
  defaultValue: FeatureFlagConfig[Key],
): FlagInfo<Key> {
  const isEnabled = usePostHogFeatureFlagEnabled(flag);
  const variant = useFeatureFlagVariantKey(flag);
  const payload = useFeatureFlagPayload(flag);
  const { flagsWithOverrides } = useFeatureFlagOverrides();

  const isOverridden = flagsWithOverrides?.some((f) => f.flag === flag);

  if (isOverridden) {
    const overrideInfo = (flagsWithOverrides?.find((f) => f.flag === flag)
      ?.flagInfo ?? {}) as unknown as FlagInfo<Key>;
    trackFlagInSentry(flag, overrideInfo.enabled ?? false);
    return overrideInfo;
  }

  const enabled =
    isEnabled ?? (typeof defaultValue === 'boolean' ? defaultValue : false);

  trackFlagInSentry(flag, enabled);

  return {
    enabled,
    variantKey:
      typeof defaultValue === 'boolean' || typeof variant === 'boolean'
        ? undefined
        : variant,
    payload: (payload as FeatureFlagConfig[Key]) ?? defaultValue,
  };
}
